/* eslint-disable no-magic-numbers */
import { UnreachableCaseError } from 'ts-essentials';
import { JSX } from 'solid-js';

export type IconSize = 'small' | 'medium' | 'large' | 'xlarge';

export type TempSvgIcon =
  | 'svg-draw-arc'
  | 'svg-draw-circle'
  | 'svg-draw-polyline'
  | 'svg-draw-alignment'
  | 'svg-draw-rectangle'
  | 'svg-draw-polygon'
  | 'svg-draw-offset'
  | 'svg-move'
  | 'svg-scale'
  | 'svg-rotate';

export const getSvgIcon = (
  iconName: TempSvgIcon,
  iconSize: IconSize
): JSX.Element => {
  let width: number, height: number;
  switch (iconSize) {
    case 'small':
      width = 16;
      height = 16;
      break;
    case 'large':
      width = 32;
      height = 32;
      break;
    case 'xlarge':
      width = 48;
      height = 48;
      break;
    case 'medium':
    default:
      width = 24;
      height = 24;
      break;
  }

  switch (iconName) {
    case 'svg-draw-arc':
      return (
        <svg
          width={width}
          height={height}
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3.5 12.5C3.5 7.52944 7.52944 3.5 12.5 3.5"
            stroke="currentColor"
            stroke-width="1.3"
            stroke-linecap="round"
          />
          <circle cx="3.5" cy="12.5" r="1.75" fill="currentColor" />
          <circle cx="12.5" cy="3.5" r="1.75" fill="currentColor" />
        </svg>
      );
    case 'svg-draw-circle':
      return (
        <svg
          width={width}
          height={height}
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9 0.75C13.5563 0.75 17.25 4.44365 17.25 9C17.25 10.3334 16.9308 11.5914 16.3691 12.7061C16.0645 12.2887 15.6415 11.9651 15.1475 11.7852C15.5329 10.9358 15.75 9.9935 15.75 9C15.75 5.27208 12.7279 2.25 9 2.25C5.27208 2.25 2.25 5.27208 2.25 9C2.25 12.7279 5.27208 15.75 9 15.75C9.9935 15.75 10.9358 15.5329 11.7852 15.1475C11.9651 15.6415 12.2887 16.0645 12.7061 16.3691C11.5914 16.9308 10.3334 17.25 9 17.25C4.44365 17.25 0.75 13.5563 0.75 9C0.75 4.44365 4.44365 0.75 9 0.75ZM9 7.125C10.0355 7.125 10.875 7.96447 10.875 9C10.875 9.24152 10.8273 9.47157 10.7441 9.68359L13.5654 12.5049C13.7776 12.4216 14.0083 12.375 14.25 12.375C14.4859 12.375 14.711 12.4205 14.9189 12.5C14.9659 12.518 15.0116 12.538 15.0566 12.5596C15.0888 12.5749 15.1213 12.5893 15.1523 12.6064C15.1668 12.6144 15.1801 12.6244 15.1943 12.6328C15.2352 12.6567 15.2756 12.6811 15.3145 12.708C15.3254 12.7156 15.3359 12.7236 15.3467 12.7314C15.8175 13.0721 16.125 13.6245 16.125 14.25C16.125 15.2855 15.2855 16.125 14.25 16.125C13.6245 16.125 13.0721 15.8175 12.7314 15.3467C12.7236 15.3359 12.7156 15.3254 12.708 15.3145C12.6811 15.2756 12.6567 15.2352 12.6328 15.1943C12.6244 15.1801 12.6144 15.1668 12.6064 15.1523C12.5893 15.1213 12.5749 15.0888 12.5596 15.0566C12.5478 15.0321 12.5341 15.0085 12.5234 14.9834L12.459 14.8076C12.4042 14.6315 12.375 14.4441 12.375 14.25C12.375 14.0083 12.4216 13.7776 12.5049 13.5654L9.68359 10.7441C9.47157 10.8273 9.24152 10.875 9 10.875C7.96447 10.875 7.125 10.0355 7.125 9C7.125 7.96447 7.96447 7.125 9 7.125Z"
            fill="currentColor"
          />
        </svg>
      );
    case 'svg-draw-polyline':
      return (
        <svg
          width={width}
          height={height}
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3.5 12.5L12.5 3.5"
            stroke="currentColor"
            stroke-width="1.3"
            stroke-linecap="round"
          />
          <circle cx="3.5" cy="12.5" r="1.75" fill="currentColor" />
          <circle cx="12.5" cy="3.5" r="1.75" fill="currentColor" />
        </svg>
      );
    case 'svg-draw-alignment':
      // Road/rail centreline: tangent, curve, tangent with a station tick.
      return (
        <svg
          width={width}
          height={height}
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.5 13.5H5C8 13.5 8 2.5 11 2.5H13.5"
            stroke="currentColor"
            stroke-width="1.3"
            stroke-linecap="round"
          />
          <path
            d="M6.4 9.2L8.6 9.8"
            stroke="currentColor"
            stroke-width="1.1"
            stroke-linecap="round"
          />
          <circle cx="2.5" cy="13.5" r="1.5" fill="currentColor" />
          <circle cx="13.5" cy="2.5" r="1.5" fill="currentColor" />
        </svg>
      );
    case 'svg-draw-polygon':
      return (
        <svg
          width={width}
          height={height}
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9.03027 7.5C10.0656 7.50021 10.9053 8.3396 10.9053 9.375C10.9053 9.71418 10.8128 10.0312 10.6553 10.3057L13.4277 13.9307C13.4869 13.9162 13.5473 13.9056 13.6084 13.8965L13.6094 13.8955C13.6146 13.8948 13.6198 13.8943 13.625 13.8936C13.6438 13.8909 13.6627 13.8888 13.6816 13.8867C13.7452 13.8797 13.8097 13.875 13.875 13.875C13.9376 13.875 13.9995 13.8778 14.0605 13.8838C14.07 13.8847 14.0794 13.8866 14.0889 13.8877C14.1377 13.8933 14.1858 13.9009 14.2334 13.9102C14.2544 13.9142 14.2752 13.9191 14.2959 13.9238C14.3291 13.9315 14.362 13.9398 14.3945 13.9492C14.4205 13.9567 14.4462 13.9651 14.4717 13.9736C14.5023 13.9839 14.5326 13.995 14.5625 14.0068C14.5851 14.0158 14.6077 14.0244 14.6299 14.0342C14.6585 14.0468 14.6861 14.0612 14.7139 14.0752C14.7392 14.0879 14.7644 14.1005 14.7891 14.1143C14.82 14.1316 14.8501 14.15 14.8799 14.1689C14.8941 14.178 14.9089 14.1859 14.9229 14.1953L14.9316 14.2012C14.9747 14.2306 15.0163 14.2621 15.0566 14.2949H15.0557C15.4788 14.6387 15.75 15.1624 15.75 15.75C15.75 16.7854 14.9103 17.6248 13.875 17.625C13.1684 17.625 12.554 17.2336 12.2344 16.6562H12.2334C12.2321 16.654 12.2308 16.6517 12.2295 16.6494C12.1966 16.5893 12.1679 16.5268 12.1416 16.4629C12.1332 16.4426 12.1258 16.422 12.1182 16.4014C12.1007 16.3543 12.085 16.3066 12.0713 16.2578C12.044 16.1607 12.0231 16.0608 12.0117 15.958C12.0104 15.946 12.0089 15.934 12.0078 15.9219C12.0027 15.8653 12 15.808 12 15.75C12 15.704 12.0016 15.6584 12.0049 15.6133C12.0062 15.5949 12.0089 15.5768 12.0107 15.5586C12.0222 15.4458 12.0447 15.3365 12.0752 15.2305C12.0824 15.2057 12.0885 15.1806 12.0967 15.1562H12.0996C12.1364 15.0463 12.1798 14.9397 12.2354 14.8398L9.45312 11.2002C9.31713 11.2316 9.17581 11.25 9.03027 11.25C7.99474 11.25 7.15527 10.4105 7.15527 9.375C7.15527 8.33947 7.99474 7.5 9.03027 7.5ZM17.5596 6.59375L15.2998 13.5469C14.8893 13.2808 14.4006 13.1251 13.875 13.125C13.8699 13.125 13.8644 13.125 13.8594 13.125L15.7959 7.16699L9 2.22949L2.20312 7.16699L4.7998 15.1562H11.3193C11.2751 15.3472 11.25 15.5456 11.25 15.75C11.25 16.069 11.3099 16.3734 11.4141 16.6562H3.70996L0.44043 6.59375L9 0.375L17.5596 6.59375Z"
            fill="currentColor"
          />
        </svg>
      );
    case 'svg-draw-rectangle':
      return (
        <svg
          width={width}
          height={height}
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15.375 13C16.4105 13 17.25 13.8395 17.25 14.875C17.25 15.9105 16.4105 16.75 15.375 16.75C14.3395 16.75 13.5 15.9105 13.5 14.875C13.5 13.8395 14.3395 13 15.375 13ZM4.125 14.125H12.8604C12.7895 14.3628 12.75 14.6142 12.75 14.875C12.75 15.1358 12.7895 15.3872 12.8604 15.625H2.625V6.88867C2.86285 6.95953 3.11412 7 3.375 7C3.63588 7 3.88715 6.95953 4.125 6.88867V14.125ZM16.125 12.3604C15.8872 12.2895 15.6358 12.25 15.375 12.25C15.1142 12.25 14.8628 12.2895 14.625 12.3604V5.125H5.88965C5.96046 4.88723 6 4.63578 6 4.375C6 4.11422 5.96046 3.86277 5.88965 3.625H16.125V12.3604ZM3.375 2.5C4.41053 2.5 5.25 3.33947 5.25 4.375C5.25 5.41053 4.41053 6.25 3.375 6.25C2.33947 6.25 1.5 5.41053 1.5 4.375C1.5 3.33947 2.33947 2.5 3.375 2.5Z"
            fill="currentColor"
          />
        </svg>
      );
    case 'svg-draw-offset':
      return (
        <svg
          width={width}
          height={height}
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.05 1.02L12.14 1.23L13.99 2.05L15.45 3.32L16.48 4.96L16.97 6.84L16.88 8.79L16.21 10.64L15.03 12.22L10.38 16.83L9.71 16.98L9.14 16.47L9.11 16.01L9.32 15.62L14.36 10.55L14.97 9.55L15.33 8.33L15.27 6.39L14.57 4.81L13.33 3.54L11.84 2.81L10.02 2.62L9.08 2.81L8.01 3.26L6.53 4.51L2.24 8.79L1.67 8.91L1.06 8.45L1 8L1.21 7.54L6.53 2.32L8.2 1.41L10.02 1.05ZM10.32 3.66L11.57 3.81L12.45 4.2L13.45 5.05L14.09 6.12L14.33 7.24L14.18 8.64L13.81 9.49L12.87 10.61L8.89 14.59L8.35 14.77L7.89 14.4L7.95 13.83L12.26 9.55L12.81 8.85L13.05 8.24L13.11 7.06L12.69 6.06L11.93 5.3L10.75 4.87L9.83 4.93L8.86 5.39L4.16 10.06L3.64 10.12L3.25 9.76L3.37 9.15L7.92 4.6L9.05 3.93L10.29 3.69Z"
            fill="currentColor"
          />
        </svg>
      );
    case 'svg-move':
      return (
        <svg
          width={width}
          height={height}
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9 2.25V15.75M2.25 9H15.75M6 4.5L9 1.5L12 4.5M6 13.5L9 16.5L12 13.5M4.5 6L1.5 9L4.5 12M13.5 6L16.5 9L13.5 12"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      );
    case 'svg-scale':
      return (
        <svg
          width={width}
          height={height}
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="5.25"
            y="5.25"
            width="7.5"
            height="7.5"
            stroke="currentColor"
            stroke-width="1.5"
          />
          <path
            d="M2.25 7.5V2.25H7.5M15.75 10.5V15.75H10.5"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      );
    case 'svg-rotate':
      return (
        <svg
          width={width}
          height={height}
          viewBox="78 50 844 900"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g transform="translate(0, 1000) scale(1, -1)">
            <path
              d="M649 705 460 519V682Q401 672 353.5 637.0Q306 602 278.5 549.5Q251 497 251.0 436.0Q251 375 278.5 322.5Q306 270 353.5 235.0Q401 200 460 190V106Q378 116 311.5 162.5Q245 209 207 279Q168 352 168.0 436.0Q168 520 207 593Q245 664 312.0 710.0Q379 756 460 766V894ZM706 173Q633 117 543 106V190Q598 199 646 233ZM832 394Q821 304 764 232L705 291Q739 338 748 394ZM764 640Q821 567 832 478H748Q739 534 705 581Z"
              fill="currentColor"
            />
          </g>
        </svg>
      );
    default:
      throw new UnreachableCaseError(iconName);
  }
};
